import { ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { AnimatedCheckmark } from 'components/Icon/AnimatedCheckmark';
import { Body } from 'components/Text/Body';
import { TabContext } from 'containers/TabContext';
import { colors, transition } from 'theme';

const Input = styled.input<{
  $isTabbing?: boolean;
  $hideRadioButtons?: boolean;
}>`
  width: 100%;
  height: 100%;
  margin: 0;
  border: 0;
  cursor: pointer;
  && {
    appearance: none;
  }
  ${(props) =>
    !props.$isTabbing &&
    `
    &:focus {
      outline: 0;
    }
  `}
  ${(props) =>
    props.$hideRadioButtons &&
    `
    background: transparent;
    border-radius: 0;
  `}
`;
const CheckmarkContainer = styled.div<{ $checked?: boolean }>`
  background: ${colors.white};
  color: ${(props) => (props.$checked ? 'white' : colors.darkContentGrey)};
  box-shadow: 0 0 0 1px
    ${(props) => (props.$checked ? 'white' : 'rgba(0,0,0,0.5)')};

  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: all ${transition};
`;
const Container = styled.div<{
  $checked?: boolean;
  $alignTop?: boolean;
  $disabled?: boolean;
}>`
  background: ${(props) =>
    props.$checked ? colors.brandBlue : colors.inputGrey};
  color: ${(props) => (props.$checked ? 'white' : colors.darkContentGrey)};

  position: relative;
  display: flex;
  align-items: ${(props) => (props.$alignTop ? 'flex-start' : 'center')};
  padding: 20px 24px;
  height: 100%;
  border-radius: 10px;
  transition: all ${transition};
  ${(props) =>
    props.$alignTop
      ? `
    ${CheckmarkContainer} {
      margin-top: 0.25em;
    }
  `
      : ''}
  ${(props) =>
    props.$disabled
      ? `
  opacity: 0.7;
  transition: all ${transition}, opacity ${transition} 0.5s;
  * {
    cursor: not-allowed;
  }
  `
      : `
  * {
    cursor: pointer;
  }
  &:hover {
    background: ${colors.brandBlue} !important;
    color: white !important;
    ${CheckmarkContainer} {
      box-shadow: 0 0 0 1px white !important;
    }
  }
  `}
`;

type Props = {
  onClick?: () => void;
  'data-qa-id'?: string;
  id?: string;
  name?: string;
  value: string | boolean | number;
  label: ReactNode;
  checked: boolean;
  onChange?: (
    arg0: (string | null | undefined) | (boolean | null | undefined) | number,
  ) => void;
  alignTop?: boolean;
  hideRadioButtons?: boolean;
  disabled?: boolean;
};

export function LargeRadioButton({
  id,
  name,
  value,
  label,
  checked,
  onChange,
  alignTop,
  hideRadioButtons,
  disabled,
  ...props
}: Props) {
  const isTabbing = useContext(TabContext);
  return (
    <Container $checked={checked} $disabled={disabled} $alignTop={alignTop}>
      <Box top={0} left={0} position="absolute" width={1} height="100%">
        <Input
          type="radio"
          name={name}
          id={id}
          checked={checked}
          value={typeof value === 'string' ? value : value.toString()}
          onChange={(e) => {
            e.stopPropagation();

            if (onChange) {
              onChange(value);
            }
          }}
          $isTabbing={isTabbing}
          $hideRadioButtons={hideRadioButtons}
          disabled={disabled}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </Box>
      {!hideRadioButtons && (
        <CheckmarkContainer $checked={checked}>
          {checked && (
            <AnimatedCheckmark initialColorName="brandBlue" size={16} />
          )}
        </CheckmarkContainer>
      )}
      <label htmlFor={id} style={{ marginLeft: 16 }}>
        <Box component={Body} lineHeight={1.375} pointerEvents="none">
          {label}
        </Box>
      </label>
    </Container>
  );
}
